import { PROPERTY_GRID_SUBJECT_PROPERTY, noop } from '@aloft/toolkit-shared';
import { Modal } from '@aloft/toolkit-sui';

import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { fetchGetBlob } from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../hooks/api/uploadBlob.ts';
import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { setPropertyData } from '../../redux/features/propertyDataSlice.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import {
  CompPropertyDataState,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../redux/types.ts';
import { FileUploader } from '../FileUploader.tsx';

type UploadPropertyPhotoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
};

export const UploadPropertyPhotoModal = ({
  isOpen,
  onClose,
  propertyId,
}: UploadPropertyPhotoModalProps) => {
  const dispatch = useAppDispatch();
  const propertyData = useAppSelector((state) => selectPropertyDataById(state, propertyId));
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | undefined>(undefined);
  const [currentFilePreview, setCurrentFilePreview] = useState<string | undefined>(undefined);
  const [isPending, setIsPending] = useState(false);
  const context = useToolkitContext();
  const { fetcherOptions } = context;
  const toast = useToaster();

  useEffect(() => {
    const objectUrl = file ? URL.createObjectURL(file) : undefined;
    setFilePreview(objectUrl);

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file]);

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  let currentBlobId: string | undefined;
  if (isSubject) {
    currentBlobId = (propertyData as SubjectPropertyDataState)?.subjectData?.editableData
      ?.propertyIdentification?.userUploadedPhotoBlobId;
  } else {
    currentBlobId = (propertyData as CompPropertyDataState)?.compData?.editableData
      ?.propertyIdentification?.userUploadedPhotoBlobId;
  }

  useEffect(() => {
    if (!currentBlobId || file) {
      setCurrentFilePreview(undefined);
      return undefined;
    }

    let active = true;
    let objectUrl: string | undefined;

    fetchGetBlob({
      ...fetcherOptions,
      pathParams: { blobID: currentBlobId },
    })
      .then((blob) => {
        if (!active) return;
        const blobFile = new File([blob as BlobPart], 'currentPhoto.png', {
          type: blob.type as string,
        });
        objectUrl = URL.createObjectURL(blobFile);
        setCurrentFilePreview(objectUrl);
      })
      .catch((e) => {
        console.error('Failed to load current photo preview', e);
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [currentBlobId, fetcherOptions, file]);

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsPending(true);

    try {
      const blob = await uploadBlob(file, `property-photo-${propertyId}`, context);

      const blobId = blob?.id;

      if (blobId && propertyData) {
        const newData: PropertyDataState = cloneDeep(propertyData);

        if (isSubject) {
          (
            newData as SubjectPropertyDataState
          ).subjectData.editableData.propertyIdentification.userUploadedPhotoBlobId = blobId;
        } else {
          (
            newData as CompPropertyDataState
          ).compData.editableData.propertyIdentification.userUploadedPhotoBlobId = blobId;
        }

        dispatch(setPropertyData(newData));
        onClose();
        setFile(null);
      }
    } catch (error) {
      console.error(`Failed to upload photo for property ${propertyId}`, error);

      toast({
        id: `${propertyId}-photo-upload-error`,
        type: 'error',
        title: 'Photo Upload Failed',
        body: 'There was an error uploading the photo. Please try again.',
      });
    } finally {
      setIsPending(false);
    }
  }, [context, dispatch, file, onClose, propertyData, isSubject, toast, propertyId]);

  const handleDelete = useCallback(() => {
    if (!propertyData) return;

    const newData: PropertyDataState = cloneDeep(propertyData);
    if (isSubject) {
      delete (newData as SubjectPropertyDataState).subjectData.editableData.propertyIdentification
        .userUploadedPhotoBlobId;
    } else {
      delete (newData as CompPropertyDataState).compData.editableData.propertyIdentification
        .userUploadedPhotoBlobId;
    }

    dispatch(setPropertyData(newData));
    onClose();
  }, [dispatch, onClose, propertyData, isSubject]);

  const onSave = () => {
    handleUpload().then(noop).catch(console.error);
  };

  const onFileDelete = () => {
    if (file) setFile(null);
    else if (currentBlobId) handleDelete();
  };

  const files = [];

  if (file) {
    files.push({
      id: 'new-file',
      name: file.name,
      blobID: undefined,
      previewUrl: filePreview,
    });
  } else if (currentBlobId) {
    files.push({
      id: currentBlobId,
      name: 'Current Photo',
      blobID: currentBlobId,
      previewUrl: currentFilePreview,
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Property Photo"
      primaryAction={{
        text: 'Save',
        onClick: onSave,
        disabled: !file || isPending,
        isLoading: isPending,
      }}
      secondaryAction={{
        text: 'Cancel',
        onClick: onClose,
        disabled: isPending,
      }}
    >
      <FileUploader
        files={files}
        onFileAdd={setFile}
        onFileDelete={onFileDelete}
        readAs="none"
        accept={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
        dropzoneMessage="Drag and drop or click to upload a photo"
      />
    </Modal>
  );
};
